import { Box, Typography, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RegisterDemoUseCommand, useProductCenterApi } from 'api';
import { installationKeys, searchBusinessPartnersQuery } from 'api/queries';
import { unitKeys } from 'api/queries/UnitQueries';
import { Id } from 'domain/Id';
import { PageButtons } from 'library/components/controls/buttons/PageButtons';
import { ControlledAutocomplete } from 'library/components/controls/form/ControlledAutocomplete';
import { ControlledDatePicker } from 'library/components/controls/form/ControlledDatePicker';
import { StyledLink } from 'library/components/link/CustomLink';
import { volvoWOPUrl } from 'library/components/link/linkUtils';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';
import { usePrompt } from 'library/hooks/useRouterNavigation';
import { useValidationErrorsSnackbar } from 'library/hooks/useValidationErrorsSnackbar';
import { useAppSelector } from 'library/redux/hooks';
import { OperatingLocationFormType } from 'modules/Shared/Components/OperatingLocation/OperatingLocationForm';
import { OperatingLocationFormContent } from 'modules/Shared/Components/OperatingLocation/OperatingLocationFormContent';
import { WarrantyWarningAlert } from 'modules/Shared/Components/Warranty/WarrantyWarningAlert';

import { selectWarrantyErrors } from '../State';

type UnregisterDemoDealerUsageFormType = FormDemoDealerUsage &
	OperatingLocationFormType;

interface FormDemoDealerUsage {
	registrationDate: DateTime | null;
	unitUserId: number | null;
}

const toRegisterDemoDealerUsageCommand = (
	unitId: number,
	v: UnregisterDemoDealerUsageFormType
): RegisterDemoUseCommand => {
	const {
		registrationDate: controlledDatePicker,
		unitUserId: controlledAutocomplete,
		...location
	} = v;

	if (!controlledDatePicker) {
		throw new Error('Demo start date is required');
	}

	if (!controlledAutocomplete) {
		throw new Error('Customer is required');
	}

	return {
		unitId,
		registerDemoUse: {
			demoStart: controlledDatePicker?.toJSDate(),
			customerNumber: controlledAutocomplete,
		},
		location,
	};
};

type RegisterDemoDealerUsageProps = Id & {
	cancelAction?: () => void;
};

export const RegisterDemoDealerUsage = ({
	id,
	cancelAction,
}: RegisterDemoDealerUsageProps) => {
	const { t } = useTranslation([
		'unitOverview',
		'common',
		'eventLog',
		'commonButton',
	]);
	const { api } = useProductCenterApi();
	const queryClient = useQueryClient();
	const { enqueueValidationSnackbar } = useValidationErrorsSnackbar();
	const { trackFormSubmitSuccess } = useAdobeTracking();

	const warrantyErrors = useAppSelector(selectWarrantyErrors);
	const theme = useTheme();

	const [searchPhrase, setSearchPhrase] = useState('');

	const form = useForm<UnregisterDemoDealerUsageFormType>({
		defaultValues: {
			registrationDate: DateTime.local(),
			unitUserId: null,
			country: '',
			state: '',
			city: '',
			machineVesselLocation: '',
			zipCode: '',
			address: '',
		},
	});

	const { control, handleSubmit } = form;

	const { isDirty } = form.formState;

	const registerDemoDealerUsageMutation = useMutation<
		false | void,
		unknown,
		RegisterDemoUseCommand
	>({
		mutationFn: async ({
			unitId,
			registerDemoUse,
			location,
		}: RegisterDemoUseCommand) => {
			await api.registerDemoUseUnit(unitId.toString(), {
				unitId,
				registerDemoUse,
				location,
			});
		},
		onError: (e) => enqueueValidationSnackbar(e as Error),
		onSuccess: async (_data, variables) => {
			await queryClient.invalidateQueries({
				queryKey: unitKeys.single(variables.unitId),
			});

			await queryClient.invalidateQueries({
				queryKey: installationKeys.demoUses(variables.unitId),
			});

			void trackFormSubmitSuccess({
				type: 'unit',
				name: 'Unit Register Demo Dealer Usage',
				id: variables.unitId.toString(),
				destination: 'unit-overview',
				topic: 'unit-register-demo-dealer-usage-complete',
			});
		},
	});

	const isSaving = registerDemoDealerUsageMutation.isPending;

	const handleFormSubmit = handleSubmit((values) => {
		void registerDemoDealerUsageMutation.mutateAsync(
			toRegisterDemoDealerUsageCommand(Number(id), values)
		);
	});

	//Search unit users
	const { data: rows = [], isLoading } = useQuery(
		searchBusinessPartnersQuery(api, searchPhrase)
	);

	const Prompt = usePrompt(
		t('common:unsaved-warning-dialog-message'),
		isDirty
	);

	const partnerOptions = rows.map((x) => ({
		...x,
		id: x.customerNumber.toString(),
	}));

	return (
		<>
			<WarrantyWarningAlert errors={warrantyErrors} />
			<FormProvider {...form}>
				<form noValidate onSubmit={handleFormSubmit}>
					<Box
						sx={{
							display: 'grid',
							marginTop: '2em',
							marginBottom: '1em',
							gridTemplateColumns: {
								xs: 'repeat(1, 1fr)',
								sm: '1fr 2fr',
								md: '1fr 2fr',
							},
							gap: 2,
							p: 0,
						}}>
						<ControlledDatePicker
							control={control}
							name="registrationDate"
							label={t('common:date-header')}
							validation={{
								required: 'common:validation-error-message',
							}}
							size="small"
						/>
						<ControlledAutocomplete
							id="demo-usage-user-select-autocomplete"
							control={control}
							getOptionLabel={(x) => x.customerName}
							label={t(
								'eventLog:drawer-form-label-demo-user-registered'
							)}
							loading={isLoading}
							options={partnerOptions}
							size="small"
							onInputChange={(_, value) => setSearchPhrase(value)}
							name="unitUserId"
							validation={{
								required: 'common:validation-error-message',
							}}
						/>
					</Box>

					<Typography>
						{t('unitOverview:demo-usage-registered-details')}
						<StyledLink
							href={volvoWOPUrl}
							target="_blank"
							sx={{
								marginLeft: 0.5,
								color: theme.palette.secondary.main,
							}}>
							{t(
								'unitOverview:demo-usage-registered-details-link-text'
							)}
						</StyledLink>
					</Typography>

					<Typography
						sx={{
							marginTop: '2em',
							fontSize: theme.typography.h4,
						}}>
						{t('unitOverview:header-validates-units-location')}
					</Typography>
					<hr />
					<OperatingLocationFormContent />
					<PageButtons
						primaryButtonLabel={t('commonButton:save')}
						secondaryButtonLabel={t('commonButton:cancel')}
						secondaryButtonAction={cancelAction}
						isPrimaryButtonSubmitType
						displayLoadingIndicator={isSaving}
						primaryButtonDisabled={!isDirty || isSaving}
					/>
					{Prompt}
				</form>
			</FormProvider>
		</>
	);
};
